import { Edit, AddCircle as PlusCircle, Settings as SettingsIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { Icons } from '@/components/icons';
import { Page } from '@/components/page';
import { SecureImage } from '@/components/secure-image';
import { SubscriptionLimitGate } from '@/components/subscription/subscription-limit-gate';
import { useLocationAccessLayer } from '@/features/locations';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { EntitySummary } from '@/lib/types';
import { EntityListSortMenu } from '@/components/entity-list-sort-menu';
import { useEntityListSort } from '@/hooks/use-entity-list-sort';

export const Route = createFileRoute('/locations/')({
  component: LocationsPage,
});

function LocationsContent() {
  const { novel } = useNovelRepository();
  const navigate = useNavigate();
  const { locations, isLoading } = useLocationAccessLayer();
  const resolveResponsive = useResponsiveValue();
  const { t } = useTranslation();
  const { getSummaries } = useNovelRepository();

  const { sort, setSort, sortItems } = useEntityListSort('locations');
  const sortedLocations = sortItems(getSummaries<EntitySummary>('locations')); 

  return (
    <AuthGuard>
      <Page
        title={t('entity_list.locations.title')}
        description={t('entity_list.locations.description')}
        layoutMode="scrolling"
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EntityListSortMenu value={sort} onChange={setSort} />
            <Tooltip title={t('entity_list.locations.settings')}>
              <IconButton
                onClick={() => navigate({ to: '/locations/settings' })}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
                  borderRadius: 2,
                }}
              >
                <SettingsIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <SubscriptionLimitGate limit="locationsPerNovel" currentCount={sortedLocations.length}>
              <Button
                id="new-location-step"
                variant="contained"
                component={Link}
                to="/locations/new"
                disabled={!novel}
                startIcon={<PlusCircle sx={{ fontSize: 18 }} />}
                sx={{ borderRadius: 2 }}
              >
                {t('entity_list.locations.new')}
              </Button>
            </SubscriptionLimitGate>
          </Box>
        }
      >

        {isLoading && !novel.id && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!novel.id && !isLoading && (
          <Card sx={{ maxWidth: 500, mx: 'auto', mt: 4, textAlign: 'center', p: 4, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h2" gutterBottom>{t('entity_list.no_novel_title')}</Typography>
              <Typography variant="body1" color="text.secondary">
                {t('entity_list.no_novel_desc', { entity: t('sidebar.geography').toLowerCase() })}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Grid container spacing={3}>
          {novel && sortedLocations.map((location) => (
            <Grid key={location.id} size={resolveResponsive({ xs: 6, sm: 4, lg: 3, xl: 2 })}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px -10px rgba(0,0,0,0.15)',
                    '& .MuiCardMedia-root img': {
                      transform: 'scale(1.05)',
                    }
                  },
                }}
              >
                <Link
                  to="/locations/view/$id"
                  params={{ id: location.id }}
                  style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                  <CardMedia
                    sx={{
                      aspectRatio: '1 / 1',
                      position: 'relative',
                      bgcolor: 'action.hover',
                      overflow: 'hidden',
                    }}
                  >
                    {location.mainImageUrl ? (
                      <SecureImage
                        src={location.mainImageUrl}
                        path={location.mainImagePath}
                        alt={location.name}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.5s',
                        }}
                      />
                    ) : location.mapImageUrl ? (
                      <SecureImage
                        src={location.mapImageUrl}
                        path={location.mapImageStoragePath || undefined}
                        alt={location.name}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.5s',
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.1 }}>
                        <Icons.locations width={80} height={80} />
                      </Box>
                    )}
                  </CardMedia>
                  <CardContent sx={{ flexGrow: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: '1.25rem',
                        mb: 1,
                        color: 'text.primary',
                        pr: 5, // Avoid overlap with edit button
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {location.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.5,
                      }}
                    >
                      {location.shortDescription}
                    </Typography>
                    {location.updatedAt && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 'auto',
                          pt: 2,
                          color: 'text.disabled',
                          fontSize: '0.75rem',
                        }}
                      >
                        {t('entity_list.locations.updated_at')}: {new Date(location.updatedAt).toLocaleString()}
                      </Typography>
                    )}
                  </CardContent>
                </Link>

                {/* Edit Button - Outside the main link but inside the card */}
                <Tooltip title={t('entity_list.locations.edit')}>
                  <IconButton
                    onClick={() => navigate({ to: '/locations/edit/$id', params: { id: location.id } })}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      bgcolor: 'background.paper',
                      boxShadow: 1,
                      zIndex: 2,
                      '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                      },
                    }}
                    size="small"
                  >
                    <Edit sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Page>
    </AuthGuard>
  );
}

function LocationsPage() {
  return (
    <Suspense fallback={
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    }>
      <LocationsContent />
    </Suspense>
  );
}
